import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, switchMap, take, Subject, map } from 'rxjs';
import { environment } from '@environments/environment';
import { OnboardingOrderOutIntegration, OnboardingOrderOutIntegrationForm, OnboardingOrderOutIntegrationUpsert } from '@core/models/integrations/orderOut';
import {
  ResponseSuccess,
  ResponseObjectSuccess,
} from '@interfaces/response-success';
import { Store } from '@ngrx/store';
import { selectCredential, selectIntegrationList } from '@store/selectors';
import { Credential } from '@core/models';

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  credential$!: Observable<Credential | null>;
  integrationList$: Observable<OnboardingOrderOutIntegration[] | null>;

  private createCompletedSource = new Subject<void>();
  createCompleted$ = this.createCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private removeCompletedSource = new Subject<void>();
  removeCompleted$ = this.removeCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.integrationList$ = this.store.select(selectIntegrationList);
  }

  createCompleted(): void {
    this.createCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  removeCompleted(): void {
    this.removeCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseSuccess<OnboardingOrderOutIntegration>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<OnboardingOrderOutIntegration>>(
          `${environment.microDeliverect}/api/v1/integrations/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  findAll(storeId?: string | null): Observable<ResponseSuccess<OnboardingOrderOutIntegration>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!storeId && { localId: storeId }),
        };

        return this.http.get<ResponseSuccess<OnboardingOrderOutIntegration>>(
          `${environment.microDeliverect}/api/v1/integrations`,
          {
            headers: { Authorization: token },
            params: {
              ...params,
            }
          }
        );
      }),
      take(1)
    );
  }

  create(integrationForm: OnboardingOrderOutIntegrationUpsert): Observable<ResponseObjectSuccess<OnboardingOrderOutIntegration>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<OnboardingOrderOutIntegration>>(
          `${environment.microDeliverect}/api/v1/store/orderout/onboarding`,
          integrationForm,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  update(
    id: string,
    integrationForm: OnboardingOrderOutIntegrationUpsert
  ): Observable<ResponseObjectSuccess<OnboardingOrderOutIntegration>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<OnboardingOrderOutIntegration>>(
          `${environment.microDeliverect}/api/v1/integrations/${id}`,
          integrationForm,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  remove(id: string): Observable<ResponseObjectSuccess<Response>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseObjectSuccess<Response>>(
          `${environment.microDeliverect}/api/v1/integrations/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }
}
